import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation, useRouteError } from "react-router-dom";

import { AppShell } from "@/app/AppShell";
import { useAuth } from "@/features/auth/AuthContext";
import { ForgotPasswordPage, RegisterPage, ResetPasswordPage, SignInPage } from "@/features/auth/AuthPages";
import { LogoMark } from "@/shared/ui/brand";
import { EmptyState, ErrorState, PageHeader } from "@/shared/ui/layout";
import { LinkButton, Spinner } from "@/shared/ui/primitives";

const RELOADED_FOR_UPDATE = "pl-reloaded-for-update";

/** Lazy page. A tab opened before a deploy asks for code files the new build no
 * longer has; reload once to pick up the new version instead of failing. */
const page = (factory: () => Promise<Record<string, unknown>>, name: string) =>
  lazy(async () => {
    try {
      const module = await factory();
      sessionStorage.removeItem(RELOADED_FOR_UPDATE);
      return { default: module[name] as React.ComponentType };
    } catch (error) {
      if (!sessionStorage.getItem(RELOADED_FOR_UPDATE)) {
        sessionStorage.setItem(RELOADED_FOR_UPDATE, "1");
        window.location.reload();
        return new Promise<never>(() => undefined);
      }
      throw error;
    }
  });

const TodayPage = page(() => import("@/features/today/TodayPage"), "TodayPage");
const PathsPage = page(() => import("@/features/paths/PathsPage"), "PathsPage");
const NewPathPage = page(() => import("@/features/paths/NewPathPage"), "NewPathPage");
const PathOverviewPage = page(() => import("@/features/paths/PathOverviewPage"), "PathOverviewPage");
const ReviewPage = page(() => import("@/features/paths/ReviewPage"), "ReviewPage");
const ModulePage = page(() => import("@/features/paths/ModulePage"), "ModulePage");
const LessonPage = page(() => import("@/features/lesson/LessonPage"), "LessonPage");
const AssessmentPage = page(() => import("@/features/assessments/AssessmentPage"), "AssessmentPage");
const CalendarPage = page(() => import("@/features/calendar/CalendarPage"), "CalendarPage");
const JournalPage = page(() => import("@/features/journal/JournalPage"), "JournalPage");
const ProgressPage = page(() => import("@/features/progress/ProgressPage"), "ProgressPage");
const ResourcesPage = page(() => import("@/features/resources/ResourcesPage"), "ResourcesPage");
const ContentPlannerPage = page(() => import("@/features/publications/ContentPlannerPage"), "ContentPlannerPage");
const SettingsPage = page(() => import("@/features/settings/SettingsPage"), "SettingsPage");
const OnboardingPage = page(() => import("@/features/onboarding/OnboardingPage"), "OnboardingPage");
const AdminUsersPage = page(() => import("@/features/admin/AdminPages"), "AdminUsersPage");
const AdminUserPage = page(() => import("@/features/admin/AdminPages"), "AdminUserPage");

function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center" aria-busy="true">
      <div className="flex flex-col items-center gap-4">
        <LogoMark className="size-12" />
        <Spinner label="Loading Person Learn" />
      </div>
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="py-16"><Spinner /></div>}>{children}</Suspense>;
}

function RequireAuth() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />;
  if (!user.onboarding_completed_at && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}

/** Administrator pages; everyone else sees "not found", not that they exist. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return user?.role === "admin" ? <>{children}</> : <NotFoundPage />;
}

function RouteError() {
  const error = useRouteError();
  return (
    <div className="mx-auto max-w-xl px-4 py-16">
      <ErrorState error={error} title="This page hit a problem" onRetry={() => window.location.reload()} />
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="py-8">
      <PageHeader title="Page not found" />
      <EmptyState title="We couldn't find that page" action={<LinkButton to="/today">Go to Today</LinkButton>}>
        The link may be out of date, or the item may have been removed.
      </EmptyState>
    </div>
  );
}

export const router = createBrowserRouter([
  { path: "/signin", element: <SignInPage />, errorElement: <RouteError /> },
  { path: "/register", element: <RegisterPage />, errorElement: <RouteError /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password", element: <ResetPasswordPage /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      { path: "/onboarding", element: <Lazy><OnboardingPage /></Lazy> },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/today" replace /> },
          { path: "/today", element: <Lazy><TodayPage /></Lazy> },
          { path: "/paths", element: <Lazy><PathsPage /></Lazy> },
          { path: "/paths/new", element: <Lazy><NewPathPage /></Lazy> },
          { path: "/paths/:pathId", element: <Lazy><PathOverviewPage /></Lazy> },
          { path: "/paths/:pathId/review", element: <Lazy><ReviewPage /></Lazy> },
          { path: "/paths/:pathId/modules/:moduleKey", element: <Lazy><ModulePage /></Lazy> },
          { path: "/paths/:pathId/lessons/:lessonKey", element: <Lazy><LessonPage /></Lazy> },
          { path: "/assessments/:assessmentId", element: <Lazy><AssessmentPage /></Lazy> },
          { path: "/calendar", element: <Lazy><CalendarPage /></Lazy> },
          { path: "/discoveries", element: <Lazy><JournalPage /></Lazy> },
          { path: "/progress", element: <Lazy><ProgressPage /></Lazy> },
          { path: "/resources", element: <Lazy><ResourcesPage /></Lazy> },
          { path: "/content", element: <Lazy><ContentPlannerPage /></Lazy> },
          { path: "/settings", element: <Lazy><SettingsPage /></Lazy> },
          { path: "/admin", element: <RequireAdmin><Lazy><AdminUsersPage /></Lazy></RequireAdmin> },
          { path: "/admin/users/:userId", element: <RequireAdmin><Lazy><AdminUserPage /></Lazy></RequireAdmin> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
